import type { EntityType } from '../../../../core/models/entity-type.model';
import type { Entity } from '../../../../core/models/entity.model';
import { resolveComponentType } from '../../../../entity-editor/utils/property-helpers';
import type { Property, PropertyItem, PropertyRules } from '../../../../core/models/property.model';
import { PropertyFlags } from '../../../../core/constants/property-flags';

interface PropertyWithEntity {
  entity: Entity;
  property: Property;
}

export interface CombinedPropertyDetails {
  values: { productId: string; value: unknown }[];
  measurementUnit?: string;
  combinedValue: unknown;
  valuesDiffer: boolean;
  items: PropertyItem[];
}

export interface CombinedMultiEditProduct {
  entity: Entity;
  propertyDetails: Record<string, CombinedPropertyDetails>;
}

export const combineMultiEditProduct = (entityType: EntityType, entitiesToCombine: Entity[]): CombinedMultiEditProduct => {
  const combinedEntity: Entity = {
    Id: 'client',
    EntityTypeKey: entityType.Key,
    ParentId: null,
    Groups: {},
    Properties: {}
  };

  const propertyDetails: Record<string, CombinedPropertyDetails> = {};

  for (const groupType of entityType.Groups) {
    combinedEntity.Groups[groupType.Key] = {
      Key: groupType.Key,
      Rules: {
        VisibleIf: entitiesToCombine.every(entity => entity.Groups[groupType.Key].Rules.VisibleIf)
      }
    };

    for (const propertyType of groupType.Properties) {
      const resolvedPropertyType = resolveComponentType(entitiesToCombine[0], propertyType);

      // Buttons should not be enabled in multi edit
      const isButton = resolvedPropertyType === 'button' || resolvedPropertyType === 'boolean-button';
      const notEditableInMultiEdit = isButton || propertyType.Flags.includes(PropertyFlags.noMultiEdit);

      const propertiesWithEntity: PropertyWithEntity[] = entitiesToCombine.map(entity => {
        return {
          entity,
          property: entity.Properties[propertyType.Key]
        };
      });

      const properties = propertiesWithEntity.map(x => x.property);

      const combinedValueResult = combinePropertyValues(propertiesWithEntity);

      const propertyItems = resolvedPropertyType === 'select' ? intersectPropertyItemLists(properties.map(prop => prop.Items ?? [])) : [];
      combinedValueResult.items = propertyItems;

      propertyDetails[propertyType.Key] = combinedValueResult;

      combinedEntity.Properties[propertyType.Key] = {
        Key: propertyType.Key,
        MeasurementUnit: properties[0].MeasurementUnit,
        Items: propertyItems,
        Value: combinedValueResult.combinedValue,
        Rules: intersectPropertyRules(
          properties.map(p => p.Rules),
          notEditableInMultiEdit
        )
      };
    }
  }

  return {
    entity: combinedEntity,
    propertyDetails
  };
};

const combinePropertyValues = (properties: PropertyWithEntity[]): CombinedPropertyDetails => {
  const propertyValues = [...new Set(properties.map(p => p.property.Value))];
  const allPropertiesHaveSameValue = propertyValues.length === 1;

  const measurementUnit = properties[0].property.MeasurementUnit;

  return {
    values: properties.map(({ property, entity }) => ({ value: property.Value, productId: entity.Id })),
    measurementUnit: measurementUnit?.Key,
    combinedValue: allPropertiesHaveSameValue ? propertyValues[0] : null,
    valuesDiffer: !allPropertiesHaveSameValue,
    items: []
  };
};

/**
 * Intersects the property rules from multiple properties
 * @param rules Rule objects
 * @param disable Should we force disable for the property
 */
const intersectPropertyRules = (rules: PropertyRules[], disable: boolean): PropertyRules => {
  /**
   * Internal helper. Gets a clamped min/max value rule result
   * @param key Rule name
   * @param max Get max value or min value
   */
  const getNumericRuleValue = (key: keyof PropertyRules, max: boolean): number | undefined => {
    if (rules.some(r => r[key] !== null && r[key] !== undefined)) {
      const values = rules.map(r => (r[key] as number) ?? 0);
      return max ? Math.max(...values) : Math.min(...values);
    }
    return undefined;
  };

  return {
    RequiredIf: rules.some(r => r.RequiredIf),
    ReadOnlyIf: disable || rules.some(r => r.ReadOnlyIf),
    VisibleIf: rules.every(r => r.VisibleIf),
    EnableIf: !disable && rules.some(r => r.EnableIf),
    SetMinValue: getNumericRuleValue('SetMinValue', true),
    SetMaxValue: getNumericRuleValue('SetMaxValue', false),
    SetMinLength: getNumericRuleValue('SetMinLength', true),
    SetMaxLength: getNumericRuleValue('SetMaxLength', false)
  };
};

/**
 * Creates an intersection of all the property items lists which only includes the options that are available in all the provided lists.
 * @param lists List of property item lists
 */
const intersectPropertyItemLists = (lists: PropertyItem[][]): PropertyItem[] => {
  // Simple cases: only one list or no lists -> return
  if (lists.length === 1) {
    return lists[0];
  }
  if (lists.length === 0) {
    return [];
  }

  // Collection
  const result: PropertyItem[] = [];

  /*
   * Get the first list in the collection - all items that will be included must be contained in every list, therefore
   * we can safely use the first list as a base to iterate through.
   */
  const [firstList, ...otherLists] = lists;

  /*
   * Iterate through the items in the first list and add them to the result if the value exists in all other lists.
   */
  firstList.forEach(item => {
    if (otherLists.every(list => list.some(i => i.Value === item.Value))) {
      result.push(item);
    }
  });

  return result;
};
